import {
  Article,
  CheckoutStateProps,
  Discounts,
  Fee,
  Price,
  TogglesAndExperiments,
} from '@zooplus/checkout-state';
import {
  Article as EventArticle,
  Discount as EventDiscount,
  EXPERIMENT_TOOL,
  Fee as EventFee,
} from '@zooplus/tracker-listener';
import { ExperimentData } from '@zooplus/tracker-listener/dist/es/types/models/experiment';

import { getAppConfig } from '@config/get-app-config';

import { CUSTOMER_STATUS, CUSTOMER_TYPE, LINE_ITEM_DISCOUNT } from './constants';
import { isArrayNotEmpty, transformType } from './utils';

export const getCartArticles = (
  articles: Article[],
  isCartApiV2: boolean,
  cartSubscriptionInterval?: number,
): EventArticle[] => {
  return articles.map((article: Article): EventArticle => {
    const articleDiscounts = isCartApiV2
      ? getArticleDiscountsForCartV2(article, cartSubscriptionInterval)
      : getArticleDiscountsForCartV1(article, cartSubscriptionInterval);

    const cartVersionSpecificArticleFields = isCartApiV2
      ? getArticleFieldsForCartV2(article)
      : getArticleFieldsForCartV1(article);

    return {
      sku: article.shopId,
      productShopId: article.psId,
      title: `${article.name} ${article.variantName}`,
      brand: [],
      category: (article.category ?? '').split(','),
      type: transformType(article.type) as EventArticle['type'],
      bestPriceArticle: article.bestPriceArticle,
      quantity: article.quantity,
      bonusPointsPrice: article.bonusPoints,
      totalBonusPoints: article.grossBonusPoints,
      seller: getSeller(article.shipmentId),
      discounts: articleDiscounts,
      totalDiscount: getTotalArticleDiscount(articleDiscounts),
      ...cartVersionSpecificArticleFields,
    };
  });
};

export const getCartDiscounts = (discounts: Discounts[], isCartApiV2: boolean) => {
  if (!isArrayNotEmpty(discounts)) {
    return [];
  }

  return discounts.map((discount: Discounts): EventDiscount => {
    let selCode = discount.selCode ?? '';
    if (isCartApiV2) {
      selCode = discount.couponDetails?.selCode ?? '';
    }

    return {
      selCode,
      type: transformType(discount.type) as EventDiscount['type'],
      value: Math.abs(discount.subtotal),
      applicableArticles:
        discount.eligibleArticles as unknown as EventDiscount['applicableArticles'],
    };
  });
};

export const getCartFees = (fees: Fee[]) => {
  if (!isArrayNotEmpty(fees)) {
    return [];
  }

  return fees.map(({ type, absolutePrice }: Fee): EventFee => {
    return {
      type: transformType(type) as EventFee['type'],
      value: absolutePrice,
    };
  });
};

export const getArticleDiscountsForCartV1 = (article: Article, cartSubscriptionInterval = 0) => {
  const articleDiscounts: EventArticle['discounts'] = [];
  if (article.subscriptionApplied && article.subscriptionDetails) {
    const { percentage } = article.subscriptionDetails;
    addSubscriptionDiscountToArticle(articleDiscounts, {
      applied: true,
      value: 0,
      frequency: String(cartSubscriptionInterval),
      percentageValue: percentage,
    });
  }

  if (article.voucherApplied && article.articleBasedVoucher) {
    const { isPersonalized = false, voucherLabel = '' } = article.articleBasedVoucher;
    addArticleBasedDiscountToArticle(articleDiscounts, {
      isPersonalized,
      applied: true,
      value: 0,
      percentageValue: parseFloat(voucherLabel),
    });
  }

  return articleDiscounts;
};

export const getArticleDiscountsForCartV2 = (article: Article, cartSubscriptionInterval = 0) => {
  const articleDiscounts: EventArticle['discounts'] = [];
  const { lineItemDiscounts = {} } = article;

  if (lineItemDiscounts.articleBasedDiscount) {
    const {
      applied = false,
      isPersonalized = false,
      subTotal = 0,
      value: percentageValue,
    } = lineItemDiscounts.articleBasedDiscount;

    addArticleBasedDiscountToArticle(articleDiscounts, {
      applied,
      isPersonalized,
      percentageValue,
      value: subTotal,
    });
  }

  if (lineItemDiscounts.autoshipmentDiscount) {
    const { applied = false, subTotal = 0, percentage } = lineItemDiscounts.autoshipmentDiscount;
    addSubscriptionDiscountToArticle(articleDiscounts, {
      applied,
      value: subTotal,
      frequency: String(cartSubscriptionInterval),
      percentageValue: percentage,
    });
  }

  return articleDiscounts;
};

export const getCommonTrackingData = () => {
  const { site, deviceType } = getAppConfig();
  return {
    locale: site.locale,
    domain: site.domain,
    siteId: site.id,
    currencyCode: site.alternativeCurrency,
    pageLocation: window.location.href,
    platform: deviceType,
  };
};

const getSeller = (shipmentId?: number): EventArticle['seller'] => ({
  type: shipmentId && shipmentId !== 1 ? 'THIRD_PARTY' : 'FIRST_PARTY',
});

type OtherArticleFields = Pick<
  EventArticle,
  'unitPrice' | 'totalPrice' | 'autoshipmentSelected' | 'autoshipmentApplied'
>;

const getArticleFieldsForCartV1 = (article: Article): OtherArticleFields => {
  return {
    unitPrice: article.actualPrice,
    totalPrice: article.subtotal,
    autoshipmentSelected: article.subscriptionSelected,
    autoshipmentApplied: article.subscriptionApplied,
  };
};

const getArticleFieldsForCartV2 = (article: Article): OtherArticleFields => {
  return {
    unitPrice: (article.price as Price)?.value, // @ts-expect-error "cart v2 have a property called subTotal instead of subtotal"
    totalPrice: article['subTotal'], // @ts-expect-error "cart v2 have a property called autoshipmentSelected instead of subscriptionSelected"
    autoshipmentSelected: article.autoshipmentSelected, // @ts-expect-error "cart v2 have a property called autoshipmentApplied instead of subscriptionApplied"
    autoshipmentApplied: article.autoshipmentApplied,
  };
};

const getTotalArticleDiscount = (articleDiscounts: EventArticle['discounts'] = []) => {
  const initialValue = 0;
  return articleDiscounts.reduce(
    (accumulator, currentDiscount) => accumulator + (currentDiscount.value ?? 0),
    initialValue,
  );
};

const addSubscriptionDiscountToArticle = (
  articleDiscounts: EventArticle['discounts'] = [],
  discountDetails: {
    applied: boolean;
    value: number;
    frequency: string;
    percentageValue?: number | null;
  },
) => {
  const { applied = false, value, frequency, percentageValue } = discountDetails;

  articleDiscounts.push({
    type: LINE_ITEM_DISCOUNT.SUBSCRIPTION,
    available: true,
    applied,
    value,
    frequency,
    ...(percentageValue && { percentageValue }),
  });

  return articleDiscounts;
};

const addArticleBasedDiscountToArticle = (
  articleDiscounts: EventArticle['discounts'] = [],
  discountDetails: {
    isPersonalized: boolean;
    applied: boolean;
    value: number;
    percentageValue?: number | null;
  },
) => {
  const { isPersonalized = false, applied = false, value, percentageValue } = discountDetails;

  articleDiscounts.push({
    type: isPersonalized ? LINE_ITEM_DISCOUNT.P_ABD : LINE_ITEM_DISCOUNT.ABD,
    available: true,
    applied,
    value,
    ...(percentageValue && { percentageValue }),
  });

  return articleDiscounts;
};

export const getFormattedExperiments = (experiments: TogglesAndExperiments[]): ExperimentData[] => {
  if (!Array.isArray(experiments)) {
    return [];
  }
  return experiments.map(({ id, value }) => {
    return {
      tool: EXPERIMENT_TOOL.OPTIMIZELY,
      id,
      value,
    };
  });
};

export const extractCheckoutDetails = (checkoutState: CheckoutStateProps) => {
  const { sid } = checkoutState?.cart || {};
  const { customerId, customerType, trueNewCustomerStatus } = checkoutState?.customer || {};

  const id = customerId?.toString();
  const tnc = trueNewCustomerStatus === CUSTOMER_STATUS.TNC;
  const guest = customerType === CUSTOMER_TYPE.GUEST_CUSTOMER;

  return {
    sid,
    id,
    guest,
    tnc,
  };
};
